// On-device document detection + crop/deskew via Scanic (Rust/WASM, ~121KB). Replaces the
// server-side OpenCV crop: the corners we detect for the LIVE OVERLAY are the same ones we use
// to crop, so the result matches what the user saw frame. Returns null/no-crop on low confidence.
//
// ⚠️ `vite dev` CANNOT run the detector: Vite's serve-transform path-normalizes scanic's inlined
// `new URL("data:application/wasm;base64,…", import.meta.url)`, collapsing `//` inside the base64
// → corrupt WASM → init fails (the capture toast will say auto-crop couldn't start). This affects
// dev ONLY (optimizeDeps.exclude doesn't help); built bundles keep the base64 intact — verified
// against the live bundle 2026-08-10. Test crop behaviour on-device or against a build.
import { Scanner, extractDocument, type CornerPoints } from "scanic";

export type P = { x: number; y: number };
export type NormQuad = { tl: P; tr: P; br: P; bl: P }; // corners as fractions 0..1 of the image

const MIN_CONFIDENCE = 0.5;

let scanner: Scanner | null = null;
let initPromise: Promise<Scanner> | null = null;
let initFailed = false;
async function getScanner(): Promise<Scanner> {
  if (scanner) return scanner;
  if (!initPromise) {
    initPromise = (async () => {
      try {
        const s = new Scanner();
        await s.initialize();
        scanner = s;
        initFailed = false;
        return s;
      } catch (e) {
        initFailed = true;
        initPromise = null; // retry on the next capture
        throw e;
      }
    })();
  }
  return initPromise;
}

/** True when the last WASM init attempt failed → captures are going out uncropped. The callers
 *  use this to WARN the user instead of failing silently (the silent no-crop fallback hid the
 *  2026-07→08 CSP breakage for weeks). */
export function detectorUnavailable(): boolean {
  return initFailed;
}

/** getScanner with ONE immediate retry. iOS suspends the page while the native camera sheet is
 *  up; an init whose fetch/compile was in flight at that moment dies and surfaces as a rejection
 *  on resume — which made the FIRST capture of every session ship uncropped while the second
 *  worked (live-tested 2026-08-10, three rounds). The failure path resets initPromise before
 *  rethrowing, so the retry here starts a FRESH init on the resumed, foreground page. */
async function getScannerRetry(): Promise<Scanner> {
  try {
    return await getScanner();
  } catch {
    return getScanner();
  }
}

// Capture-time diagnostics, shipped invisibly in the scan PDF's metadata so a failed crop in a
// live test tells us WHY without a debug build (see docscan-process-background: Keywords).
let lastDiag = "no-capture-yet";
export function lastDetectDiag(): string {
  return lastDiag;
}

/** Warm up the WASM module ahead of the first frame (best-effort). */
export function warmDetector(): void {
  void getScanner().catch(() => {});
}

/** Detect the document on a (downscaled) canvas → normalized corners + confidence, or null. */
export async function detect(canvas: HTMLCanvasElement): Promise<{ cornersNorm: NormQuad; confidence: number } | null> {
  try {
    const s = await getScannerRetry();
    const res = await s.scan(canvas, { mode: "detect" });
    const conf = res.confidence ?? 0;
    if (!res.success || !res.corners || conf < MIN_CONFIDENCE) {
      lastDiag = `no-detect(conf=${conf.toFixed(2)})`;
      return null;
    }
    const w = canvas.width || 1, h = canvas.height || 1;
    const n = (p: P): P => ({ x: p.x / w, y: p.y / h });
    return {
      cornersNorm: { tl: n(res.corners.topLeft), tr: n(res.corners.topRight), br: n(res.corners.bottomRight), bl: n(res.corners.bottomLeft) },
      confidence: conf,
    };
  } catch (e) {
    lastDiag = initFailed ? "init-failed" : `scan-error(${(e as Error)?.name ?? "?"})`;
    return null;
  }
}

// Detection scales: 1100 first (proven on real scans), 1400 as a second chance when the first
// pass finds nothing acceptable — edge detection is scale-sensitive and a retry is ~100ms.
const DETECT_EDGES = [1100, 1400];

/** A detected quad must LOOK like a photographed page before we cut with it. Confidence alone
 *  let a degenerate quad through on 2026-08-10 (table wedge kept, bottom text cut off). Checks,
 *  in pixel space: area ≥45% of the frame (stricter than the old bounding-span check — a thin
 *  diagonal sliver can span the frame with near-zero area), opposite sides within 1.4×, and all
 *  interior angles 55–125° (phone photos of a page are near-rectangular; a fold/shadow quad
 *  isn't). Reject → keep the full frame: an uncropped scan is recoverable, a miscut one is not. */
function quadLooksLikePage(q: NormQuad, W: number, H: number): boolean {
  const p = [
    [q.tl.x * W, q.tl.y * H], [q.tr.x * W, q.tr.y * H],
    [q.br.x * W, q.br.y * H], [q.bl.x * W, q.bl.y * H],
  ] as const;
  let area = 0;
  for (let i = 0; i < 4; i++) {
    const [x1, y1] = p[i]!, [x2, y2] = p[(i + 1) % 4]!;
    area += x1 * y2 - x2 * y1;
  }
  if (Math.abs(area) / 2 < 0.45 * W * H) return false;
  const len = (a: readonly number[], b: readonly number[]): number => Math.hypot(b[0]! - a[0]!, b[1]! - a[1]!);
  const top = len(p[0], p[1]), right = len(p[1], p[2]), bottom = len(p[2], p[3]), left = len(p[3], p[0]);
  if (Math.max(top, bottom) / Math.min(top, bottom) > 1.4) return false;
  if (Math.max(left, right) / Math.min(left, right) > 1.4) return false;
  for (let i = 0; i < 4; i++) {
    const a = p[(i + 3) % 4]!, b = p[i]!, c = p[(i + 1) % 4]!;
    const v1 = [a[0]! - b[0]!, a[1]! - b[1]!], v2 = [c[0]! - b[0]!, c[1]! - b[1]!];
    const ang = (Math.acos((v1[0]! * v2[0]! + v1[1]! * v2[1]!) / (Math.hypot(v1[0]!, v1[1]!) * Math.hypot(v2[0]!, v2[1]!))) * 180) / Math.PI;
    if (!(ang >= 55 && ang <= 125)) return false;
  }
  return true;
}

/** Detect the page corners on a captured photo (two scales + page-shape guard) WITHOUT cutting
 *  anything. The scan flow sends these as a hint; the server does the actual (safe, bbox) crop —
 *  on-device cutting miscut documents in the 2026-08-10 live tests and a wrong cut is
 *  unrecoverable. Null when nothing safe was found. */
export async function detectPageCorners(full: HTMLCanvasElement): Promise<{ q: NormQuad; confidence: number } | null> {
  const maxDim = Math.max(full.width, full.height);
  const attempts: string[] = [];
  for (const edge of DETECT_EDGES) {
    if (edge > maxDim && edge !== DETECT_EDGES[0]) break; // upscaled retries add nothing
    const dc = document.createElement("canvas");
    const s = Math.min(1, edge / maxDim);
    dc.width = Math.max(1, Math.round(full.width * s));
    dc.height = Math.max(1, Math.round(full.height * s));
    dc.getContext("2d", { willReadFrequently: true })!.drawImage(full, 0, 0, dc.width, dc.height);
    const d = await detect(dc);
    if (!d) { attempts.push(`${edge}:${lastDiag}`); continue; }
    if (!quadLooksLikePage(d.cornersNorm, dc.width, dc.height)) {
      attempts.push(`${edge}:guard-reject(conf=${d.confidence.toFixed(2)})`);
      continue;
    }
    lastDiag = `corners(edge=${edge},conf=${d.confidence.toFixed(2)})`;
    return { q: d.cornersNorm, confidence: d.confidence };
  }
  lastDiag = attempts.join(" ") || "no-attempt";
  return null;
}

/** Detect + cut on-device. Still used by the Receipts "+" capture (its archive has no server
 *  crop pipeline); the scan flow uses detectPageCorners + the server-side crop instead. */
export async function detectAndExtract(full: HTMLCanvasElement): Promise<HTMLCanvasElement | null> {
  const found = await detectPageCorners(full);
  if (!found) return null;
  const out = await extract(full, found.q);
  if (out) {
    lastDiag = `cropped(conf=${found.confidence.toFixed(2)})`;
    return out;
  }
  lastDiag = "extract-failed";
  return null;
}

/** Crop + deskew a full-res canvas using normalized corners → a new canvas, or null on failure. */
export async function extract(full: HTMLCanvasElement, q: NormQuad): Promise<HTMLCanvasElement | null> {
  try {
    const W = full.width, H = full.height;
    const corners: CornerPoints = {
      topLeft: { x: q.tl.x * W, y: q.tl.y * H },
      topRight: { x: q.tr.x * W, y: q.tr.y * H },
      bottomRight: { x: q.br.x * W, y: q.br.y * H },
      bottomLeft: { x: q.bl.x * W, y: q.bl.y * H },
    };
    const res = await extractDocument(full, corners, { output: "canvas" });
    return res.success && res.output instanceof HTMLCanvasElement ? res.output : null;
  } catch {
    return null;
  }
}
